Look at how u
<template>
    <v-card flat color="transparent" class="w-100 align-self-start">
        <v-card-title>
            <span class="secondary--text">Resource pools</span>
        </v-card-title>
        <v-card-text>
            <div class="d-flex align-center">
                <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" color="primary" class="mr-2">
                            <v-icon left>mdi-plus</v-icon>
                            Resource Pool
                            <v-icon right>mdi-chevron-down</v-icon>
                        </v-btn>
                    </template>
                    <v-list dense nav>
                        <v-list-item :to="{ name: 'user-pools-create-account' }">
                            <v-list-item-icon>
                                <v-icon>mdi-account-box-outline</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="mr-4">Account</v-list-item-title>
                        </v-list-item>
                        <v-list-item :to="{ name: 'user-pools-create-wallet' }">
                            <v-list-item-icon>
                                <v-icon>mdi-wallet-outline</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="mr-4">Wallet</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <v-spacer></v-spacer>

                <v-text-field
                    prepend-inner-icon="mdi-magnify "
                    autofocus
                    hide-details
                    solo
                    flat
                    dense
                    placeholder="Search for a resource pool..."
                    v-model="searchPool"
                    clearable
                    class="mr-2">
                    <template v-slot:append>
                        <PoolTypeFilterMenu v-model="selectedPoolTypes" :available-types="availablePoolTypes" />
                    </template>
                </v-text-field>

                <v-btn icon :loading="loading" @click="refreshPools">
                    <v-icon>refresh</v-icon>
                </v-btn>
            </div>

            <v-alert v-if="error" type="error" text dismissible class="mt-4 mb-0" @input="error = ''">{{ error }}</v-alert>
            <v-data-table
                :headers="headers"
                :items="filteredPools"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                :search="searchPool"
                :loading="loading"
                class="mt-4 pools-table">
                <template v-slot:no-data>
                    <span>No resource pools found.</span>
                </template>

                <template v-slot:[`item.name`]="{ item }">
                    <div class="d-flex align-center">
                        <v-icon left small>{{ item.icon }}</v-icon>
                        <span>{{ item.name }}</span>
                    </div>
                </template>
                <template v-slot:[`item.amount`]="{ item }">
                    <span v-if="item.amount !== null">{{ toLocaleFixed(item.amount, $appConfig?.VUE_APP_DASHBOARD_PRECISION) }}</span>
                    <span v-else>-</span>
                </template>
                <template v-slot:[`item.type`]="{ item }">
                    <span>{{ item.type }}</span>
                </template>
                <template v-slot:[`item.role`]="{ item }">
                    <v-chip outlined color="grey" small>{{ capitalize(item.role) }}</v-chip>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <div class="text-right">
                        <v-menu offset-y left>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn text color="secondary" small v-bind="attrs" v-on="on">
                                    <v-icon>mdi-dots-horizontal</v-icon>
                                </v-btn>
                            </template>
                            <v-list dense nav>
                                <v-list-item @click="goToPoolSettings(item)">
                                    <v-list-item-icon>
                                        <v-icon>mdi-pencil-outline</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title class="mr-4">Edit</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="goToPoolResources(item)">
                                    <v-list-item-icon>
                                        <v-icon>mdi-sitemap-outline</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title class="mr-4">Resources</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>
</template>

<script>
import { mapState } from 'vuex'
import { capitalize, toLocaleFixed } from '@/utils'
import {
    RESOURCE_POOL_FILTER_TYPES,
    isRootResourcePool,
    isSameResourcePoolId,
    resourcePoolFilterType,
    resourcePoolIcon,
    resourcePoolTypeLabel
} from '@/modules/user/components/resourcePoolMeta'

const PoolTypeFilterMenu = () => import('../components/PoolTypeFilterMenu.vue')

export default {
    name: 'UserPools',
    components: {
        PoolTypeFilterMenu
    },
    data() {
        return {
            searchPool: '',
            selectedPoolTypes: RESOURCE_POOL_FILTER_TYPES.slice(),
            itemsPerPage: 100,
            itemsPerPageOptions: [-1, 10, 50, 100],
            loading: false,
            error: '',
            headers: [
                { text: 'Name', value: 'name', align: 'left' },
                { text: 'Type', value: 'type', align: 'left' },
                { text: 'Current balance', value: 'amount', align: 'left' },
                { text: 'Parent resource pool', value: 'parentPool', align: 'left' },
                { text: 'Root account', value: 'rootAccount', align: 'left' },
                { text: 'Your role', value: 'role', align: 'left', sortable: false, width: '150px' },
                { text: 'Actions', value: 'actions', align: 'right', sortable: false }
            ]
        }
    },
    computed: {
        ...mapState(['userOrgs', 'currentOrgData']),
        ...mapState('userStore', ['selectedResourcePool', 'availableResourcePools']),
        currentOrgOid() {
            return this.$route.params.oid || this.currentOrgData?.oid || this.userOrgs[0]?.oid
        },
        availablePoolTypes() {
            return Array.from(new Set(this.availableResourcePools.map(pool => resourcePoolFilterType(pool)).filter(Boolean)))
        },
        allPools() {
            return this.availableResourcePools.map(pool => {
                const parentPool = this.availableResourcePools.find(candidate => isSameResourcePoolId(candidate.rpid, pool.parent_rpid))
                const rootPool = this.availableResourcePools.find(candidate => isSameResourcePoolId(candidate.rpid, pool.root_rpid))
                const poolType = resourcePoolFilterType(pool)
                const isRoot = isRootResourcePool(pool)

                return {
                    rpid: pool.rpid,
                    poolType,
                    type: resourcePoolTypeLabel(pool),
                    isRoot,
                    icon: resourcePoolIcon(pool),
                    name: pool.long_id,
                    amount: pool.balance ?? null,
                    parentPool: pool.parent_long_id || parentPool?.long_id || '-',
                    rootAccount: pool.root_long_id || rootPool?.long_id || (isRoot ? pool.long_id : '-'),
                    isManager: Boolean(pool.is_rp_manager),
                    role: pool.is_rp_manager ? 'manager' : 'member'
                }
            })
        },
        filteredPools() {
            return this.allPools.filter(pool => this.selectedPoolTypes.includes(pool.poolType))
        }
    },
    methods: {
        capitalize,
        toLocaleFixed,
        async refreshPools() {
            this.loading = true
            this.error = ''
            try {
                const { data: availablePools } = await this.$axios.get('/resource/available_rps')
                this.$store.commit('userStore/setAvailableResourcePoolData', availablePools)
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not load resource pools.'
            } finally {
                this.loading = false
            }
        },
        goToPoolSettings(pool) {
            if (!this.currentOrgOid || !pool.rpid) {
                return
            }

            this.$router.push({
                name: 'config-pool-general',
                params: {
                    oid: this.currentOrgOid,
                    rpid: pool.rpid
                }
            })
        },
        goToPoolResources(pool) {
            this.$router.push({ name: 'reporting-rp-overview', params: { rpid: pool.rpid } })
        }
    },
    created() {
        this.refreshPools()
    }
}
</script>

<style scoped lang="scss">
.pools-table {
    ::v-deep .v-data-table__wrapper > table {
        table-layout: fixed;
        width: 100%;
    }
}
</style>
